import { zodResolver } from '@hookform/resolvers/zod'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { useConfig } from '@/providers/config'
import { AuthLayout } from '@/ui-components/AuthLayout'
import { Button } from '@/ui-components/Button'
import { ErrorMessage, Field, Label } from '@/ui-components/Fieldset'
import { Heading } from '@/ui-components/Heading'
import { Input } from '@/ui-components/Input'
import { Text } from '@/ui-components/Text'

const formSchema = z.object({
  email: z.string().email({
    message: 'Invalid email address.',
  }),
})

const SignInPage = () => {
  const navigate = useNavigate()
  const { login, isAuthenticated } = useKindeAuth()
  const { config } = useConfig()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || '',
    },
  })

  // if the user is already authenticated, redirect them to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      // Make request to Kinde to signin
      const loginOptions = {
        orgCode: config?.kinde.org,
        connectionId: config?.kinde.connectionId,
        loginHint: values.email,
      }

      login(loginOptions)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <Heading level={1}>Welcome Back</Heading>
          <Text>Enter your credentials to sign in to your account</Text>
        </div>

        <form
          role="form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <Field>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              {...form.register('email')}
            />
            {form.formState.errors.email && <ErrorMessage>{form.formState.errors.email.message}</ErrorMessage>}
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting}
            color="primary"
          >
            Sign In
          </Button>
        </form>

        <div className="text-center">
          <Button
            plain
            disabled={form.formState.isSubmitting}
            onClick={() => navigate('/sign-up')}
          >
            Don't have an account? Sign up
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}

export default SignInPage
